import React, { useEffect, useState } from 'react';
import { referralsApi } from '../lib/api';
import { Trophy, Users, DollarSign, Medal, Loader2, RefreshCw, Copy, Check, ChevronDown, ChevronRight, CheckCircle2, Circle } from 'lucide-react';
import { toast } from 'sonner';

const rankBadge = (rank) => {
  if (rank === 1) return { icon: '🥇', class: 'bg-amber-500/15 text-amber-300 border-amber-500/30' };
  if (rank === 2) return { icon: '🥈', class: 'bg-zinc-400/15 text-zinc-200 border-zinc-400/30' };
  if (rank === 3) return { icon: '🥉', class: 'bg-orange-500/15 text-orange-300 border-orange-500/30' };
  return { icon: `#${rank}`, class: 'bg-white/5 text-zinc-400 border-white/10' };
};

export default function Referrals() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState('');
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await referralsApi.leaderboard(100);
      setData(res.data);
    } catch {
      toast.error('Не удалось загрузить рейтинг');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const copyCode = async (code, id) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedId(id);
      toast.success(`Код ${code} скопирован`);
      setTimeout(() => setCopiedId(''), 1500);
    } catch {
      toast.error('Не удалось скопировать');
    }
  };

  const board = data?.leaderboard || [];
  const totals = data?.totals || { paid_referrals: 0, unique_referrers: 0, attributed_revenue: 0 };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="referrals-page">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Trophy className="w-6 h-6 text-amber-400" />
            </div>
            <h1 className="text-3xl font-bold text-white">Реферальный рейтинг</h1>
          </div>
          <p className="text-sm text-zinc-500 max-w-2xl">
            Топ пригласивших — по числу друзей, которые уже оплатили подписку. Ищите здесь лояльных фанатов и вознаграждайте их вручную.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="referrals-refresh"
          className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-zinc-300 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Обновить
        </button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="rounded-xl bg-gradient-to-br from-amber-500/10 to-amber-500/0 border border-amber-500/20 p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-amber-300/80 mb-2">
            <Medal className="w-4 h-4" /> Активных рефереров
          </div>
          <div className="text-3xl font-bold text-white" data-testid="totals-referrers">{totals.unique_referrers}</div>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-sky-500/10 to-sky-500/0 border border-sky-500/20 p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-sky-300/80 mb-2">
            <Users className="w-4 h-4" /> Оплативших друзей
          </div>
          <div className="text-3xl font-bold text-white" data-testid="totals-paid-referrals">{totals.paid_referrals}</div>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/0 border border-emerald-500/20 p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-emerald-300/80 mb-2">
            <DollarSign className="w-4 h-4" /> Выручка от рефералов
          </div>
          <div className="text-3xl font-bold text-white" data-testid="totals-revenue">{totals.attributed_revenue.toLocaleString('ru-RU')}₽</div>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="rounded-xl border border-white/5 bg-[#0a0a0a] overflow-hidden">
        {loading && !data ? (
          <div className="p-12 flex items-center justify-center text-zinc-500">
            <Loader2 className="w-6 h-6 animate-spin mr-3" />
            Загружаем рейтинг…
          </div>
        ) : board.length === 0 ? (
          <div className="p-12 text-center" data-testid="referrals-empty">
            <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-8 h-8 text-zinc-600" />
            </div>
            <h3 className="text-white font-medium mb-2">Пока никто не привёл оплативших друзей</h3>
            <p className="text-sm text-zinc-500 max-w-md mx-auto">
              Как только первый друг оплатит подписку по чьей-то реф-ссылке, приглашающий появится здесь.
              Включите реф-программу в Настройках, если ещё не.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02] text-xs uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="w-10 px-2 py-3"></th>
                  <th className="text-left px-5 py-3 font-medium">Место</th>
                  <th className="text-left px-5 py-3 font-medium">Пользователь</th>
                  <th className="text-left px-5 py-3 font-medium">Код</th>
                  <th className="text-right px-5 py-3 font-medium">Оплатили</th>
                  <th className="text-right px-5 py-3 font-medium">Пригласил всего</th>
                  <th className="text-right px-5 py-3 font-medium">Скидка</th>
                  <th className="text-right px-5 py-3 font-medium">Принёс выручки</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {board.map((r) => {
                  const badge = rankBadge(r.rank);
                  const isOpen = !!expanded[r.telegram_id];
                  const invitees = r.invitees || [];
                  return (
                    <React.Fragment key={r.telegram_id}>
                      <tr
                        className="hover:bg-white/[0.02] transition-colors cursor-pointer"
                        onClick={() => setExpanded((prev) => ({ ...prev, [r.telegram_id]: !prev[r.telegram_id] }))}
                        data-testid={`ref-row-${r.telegram_id}`}
                      >
                        <td className="px-2 py-4 text-zinc-500">
                          {invitees.length > 0 ? (
                            isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
                          ) : null}
                        </td>
                        <td className="px-5 py-4">
                          <span className={`inline-flex items-center justify-center min-w-9 h-8 px-2 rounded-md text-sm font-semibold border ${badge.class}`}>
                            {badge.icon}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="text-white font-medium">{r.display_name || r.telegram_id}</div>
                          <div className="text-xs text-zinc-500 mt-0.5">
                            {r.username ? <>@{r.username} · </> : null}
                            <code className="text-zinc-400">{r.telegram_id}</code>
                          </div>
                        </td>
                        <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                          {r.referral_code ? (
                            <button
                              onClick={() => copyCode(r.referral_code, r.telegram_id)}
                              className="inline-flex items-center gap-1.5 px-2 h-7 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-zinc-300 font-mono"
                              data-testid={`ref-code-${r.telegram_id}`}
                            >
                              {copiedId === r.telegram_id ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                              {r.referral_code}
                            </button>
                          ) : (
                            <span className="text-zinc-600 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-5 py-4 text-right">
                          <span className="text-emerald-300 font-semibold text-base">{r.paid_referrals}</span>
                        </td>
                        <td className="px-5 py-4 text-right text-zinc-300">
                          {r.total_invited}
                        </td>
                        <td className="px-5 py-4 text-right">
                          <span className="inline-flex items-center px-2 h-6 rounded-md bg-purple-500/10 text-purple-300 border border-purple-500/20 text-xs font-medium">
                            {r.bonus_type === 'percent' ? `${r.current_bonus}%` : `${r.current_bonus}₽`}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-right text-white font-medium">
                          {r.attributed_revenue.toLocaleString('ru-RU')}₽
                        </td>
                      </tr>
                      {isOpen && (
                        <tr data-testid={`ref-invitees-${r.telegram_id}`} className="bg-black/40">
                          <td colSpan={8} className="px-8 py-4">
                            {invitees.length === 0 ? (
                              <p className="text-xs text-zinc-500 italic py-2">
                                Пока никто не перешёл по реф-ссылке этого пользователя.
                              </p>
                            ) : (
                              <div>
                                <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-2">
                                  <Users className="w-3.5 h-3.5" />
                                  Приглашённые ({invitees.length})
                                </div>
                                <div className="space-y-1">
                                  {invitees.map((inv) => (
                                    <div
                                      key={inv.telegram_id}
                                      data-testid={`invitee-${inv.telegram_id}`}
                                      className="flex items-center justify-between gap-3 py-2 px-3 rounded-md hover:bg-white/5"
                                    >
                                      <div className="flex items-center gap-3 min-w-0">
                                        {inv.has_paid ? (
                                          <span title="Оплатил" className="shrink-0 w-6 h-6 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                                          </span>
                                        ) : (
                                          <span title="Не оплатил" className="shrink-0 w-6 h-6 rounded-full bg-zinc-500/10 border border-zinc-500/20 flex items-center justify-center">
                                            <Circle className="w-3.5 h-3.5 text-zinc-500" />
                                          </span>
                                        )}
                                        <div className="min-w-0">
                                          <div className="text-sm text-white truncate">
                                            {inv.display_name}
                                          </div>
                                          <div className="text-xs text-zinc-500 truncate">
                                            {inv.username ? <>@{inv.username} · </> : null}
                                            <code>{inv.telegram_id}</code>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-4 text-sm shrink-0">
                                        <span className={`text-xs px-2 h-6 inline-flex items-center rounded-md border ${
                                          inv.has_paid
                                            ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/25'
                                            : 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
                                        }`}>
                                          {inv.has_paid ? 'Оплатил' : 'Не оплатил'}
                                        </span>
                                        <span className={`w-24 text-right ${inv.revenue > 0 ? 'text-white font-medium' : 'text-zinc-600'}`}>
                                          {inv.revenue > 0 ? `${inv.revenue.toLocaleString('ru-RU')}₽` : '—'}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
